/**
 * TUAC ScriptProcessorNode 降级方案（主线程优化版）
 * - 当浏览器不支持 AudioWorklet 时使用（如老安卓）
 * - 复用 AudioWorklet 中的 TUACProcessor 逻辑，适配为 ScriptProcessorNode
 * - 关键优化：
 *   1. 预分配所有缓冲区（避免 GC 压力）
 *   2. 在 process 回调中限制运算量
 *   3. 参数变更缓冲（不每次都更新）
 *   4. 自适应降载（检测到卡顿则关闭虚拟环绕）
 */

/**
 * 创建纯直通处理器（当 WASM 加载失败时的最终降级）
 * @param {AudioContext} ctx
 * @param {Object} options
 * @returns {{node, setParams, loadIRChannel, destroy}}
 */
function createPassthroughProcessor(ctx, options = {}) {
  const bufferSize = 4096;
  const node = ctx.createScriptProcessor(bufferSize, 2, 2);

  // 纯直通 - 输入复制到输出
  node.onaudioprocess = (event) => {
    const { inputBuffer, outputBuffer } = event;
    const inL = inputBuffer.getChannelData(0);
    const inR = inputBuffer.getChannelData(1);
    const outL = outputBuffer.getChannelData(0);
    const outR = outputBuffer.getChannelData(1);

    // 直接复制
    outL.set(inL);
    outR.set(inR);
  };

  // 返回兼容的 API（空操作）
  return {
    node,
    setParams() {
      // 无操作 - 纯直通没有参数
    },
    loadIRChannel() {
      // 无操作 - 纯直通不需要 IR
    },
    destroy() {
      try {
        node.disconnect();
      } catch (_) {}
    }
  };
}

/**
 * 创建 TUAC ScriptProcessor 降级方案
 * @param {AudioContext} ctx
 * @param {Object} options
 * @returns {Promise<{node, setParams, loadIRChannel, destroy}>}
 */
export async function createTuacScriptProcessor(ctx, options = {}) {
  const { onGlitch } = options;

  // ============ 初始化 WASM ============
  let _wasm = null;
  let _engine = null;
  let _fidelity = null;
  let _engineSurroundL = null;
  let _engineSurroundR = null;

  try {
    // 加载 WASM 模块
    // tuac_core.js 已在 public/index.html 中预加载为 ES 模块，并注册到全局
    // 这样做可以避免 Webpack "import outside src/" 的限制
    let TUACModule = null;

    // 从全局缓存读取（由 index.html 中的 <script type="module"> 预加载）
    if (typeof window !== 'undefined' && window.__TUAC_WASM_MODULE__) {
      TUACModule = window.__TUAC_WASM_MODULE__;
    } else {
      // 如果预加载失败，等待一段时间后重试（可能页面加载时序问题）
      await new Promise(resolve => setTimeout(resolve, 500));
      if (typeof window !== 'undefined' && window.__TUAC_WASM_MODULE__) {
        TUACModule = window.__TUAC_WASM_MODULE__;
      }
    }

    // 确保 TUACModule 是可用的
    if (!TUACModule || typeof TUACModule !== 'function') {
      throw new Error(
        'TUACModule not available. ' +
        `window.__TUAC_WASM_MODULE__ is ${typeof TUACModule}. ` +
        'This may indicate tuac_core.js failed to preload in index.html, ' +
        'or the page was loaded too quickly before the module script completed.'
      );
    }

    // 使用加载完的 TUACModule 初始化 WASM
    const wasmPath = '/tuac/tuac_core.wasm';
    const locateWasm = path => {
      if (path.endsWith('.wasm')) {
        return wasmPath;
      }
      return path;
    };

    _wasm = await TUACModule({ locateFile: locateWasm });

    if (!_wasm.FidelityProcessor || !_wasm.SpatialProcessor) {
      throw new Error('WASM 缺少必要的处理器');
    }

    _fidelity = new _wasm.FidelityProcessor(48000.0);
    _engine = new _wasm.SpatialProcessor();
    _engine.setAzimuth(0);

    _engineSurroundL = new _wasm.SpatialProcessor();
    _engineSurroundR = new _wasm.SpatialProcessor();
    _engineSurroundL.setAzimuth(-55);
    _engineSurroundR.setAzimuth(55);
  } catch (err) {
    // WASM 初始化失败 - 降级到纯直通模式
    console.warn('[TUAC] WASM initialization failed, using passthrough mode:', err.message);
    
    // 发送调试信息到服务器
    if (typeof window !== 'undefined' && window.fetch) {
      try {
        fetch('/api/client-debug', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            component: 'TUAC',
            event: 'WASM fallback to passthrough',
            detail: { error: err.message }
          })
        }).catch(() => {});
      } catch (_) {}
    }

    // 返回一个纯直通处理器（无 WASM）
    return createPassthroughProcessor(ctx, options);
  }

  // ============ 预分配缓冲区 ============
  const bufferSize = 4096;
  const node = ctx.createScriptProcessor(bufferSize, 2, 2);

  let _staticBuffers = null;
  let _lastFrameCount = -1;
  let _vocalFocusSmoothed = null;

  let _surroundInLPtr = 0;
  let _surroundInRPtr = 0;
  let _surroundOutLPtr = 0;
  let _surroundOutRPtr = 0;
  let _surroundBufSize = 0;

  let _tmpMid = null;
  let _tmpResLL = null;
  let _tmpResLR = null;

  // 参数缓存
  let _cache = {
    warmth: 0.3, thickness: 0.2,
    azimuth: 0, elevation: 0, distance: 1.0, roomDepth: 1.5, humidity: 0.75,
    airHighPass: 1.0, smoothing: 0.05,
    spatial: 0.5, bass: 0.0, inputGain: 1.0,
    vocalFocus: 0.6,
    eqEnabled: false
  };
  let _spatialDeconstruction = false;

  // 性能监测
  let _glitchPoints = 0;
  let _lastGlitchReportMs = 0;

  // ============ 辅助函数 ============
  function _syncAllEngines(fn) {
    if (_engine) fn(_engine);
    if (_engineSurroundL) fn(_engineSurroundL);
    if (_engineSurroundR) fn(_engineSurroundR);
  }

  function _ensureSurroundBuffers(size) {
    if (size <= _surroundBufSize) return;
    const m = _wasm._custom_malloc;
    const f = _wasm._custom_free;
    if (_surroundInLPtr) f(_surroundInLPtr);
    if (_surroundInRPtr) f(_surroundInRPtr);
    if (_surroundOutLPtr) f(_surroundOutLPtr);
    if (_surroundOutRPtr) f(_surroundOutRPtr);

    _surroundInLPtr = m(size * 4);
    _surroundInRPtr = m(size * 4);
    _surroundOutLPtr = m(size * 4);
    _surroundOutRPtr = m(size * 4);
    _surroundBufSize = size;
  }

  function _ensureDeconstructionScratch(size) {
    if (_tmpMid && _tmpMid.length === size) return;
    _tmpMid = new Float32Array(size);
    _tmpResLL = new Float32Array(size);
    _tmpResLR = new Float32Array(size);
  }

  function _copyToHeapScaled(src, dstPtr, n, gain) {
    const heap = _wasm.HEAPF32;
    const base = dstPtr >> 2;
    for (let i = 0; i < n; i++) heap[base + i] = (src[i] || 0) * gain;
  }

  function _applyHardLimiter(outL, outR, size) {
    const ceiling = 0.70710678;
    for (let i = 0; i < size; i++) {
      if (outL[i] > ceiling) outL[i] = ceiling;
      else if (outL[i] < -ceiling) outL[i] = -ceiling;
      if (outR[i] > ceiling) outR[i] = ceiling;
      else if (outR[i] < -ceiling) outR[i] = -ceiling;
    }
  }

  function _initStaticBuffers(size) {
    _releaseStaticBuffers();
    const m = _wasm._custom_malloc;
    _staticBuffers = {
      inL: m(size * 4),
      inR: m(size * 4),
      outL: m(size * 4),
      outR: m(size * 4)
    };
    _lastFrameCount = size;
  }

  function _releaseStaticBuffers() {
    if (_staticBuffers && _wasm._custom_free) {
      const f = _wasm._custom_free;
      const b = _staticBuffers;
      [b.inL, b.inR, b.outL, b.outR].forEach(p => f(p));
      _staticBuffers = null;
    }
  }

  function _detectGlitch(outL, outR, frameCount) {
    try {
      let sum = 0;
      for (let i = 0; i < frameCount; i++) {
        const l = outL[i] || 0;
        const r = outR[i] || 0;
        sum += l * l + r * r;
      }
      const rms = Math.sqrt(sum / Math.max(1, frameCount * 2));
      const thr = Math.min(0.78, Math.max(0.35, 0.35 + rms * 0.65));

      let clicks = 0;
      let prevL = 0, prevR = 0;
      for (let i = 0; i < frameCount; i++) {
        const l = outL[i] || 0;
        const r = outR[i] || 0;
        const dl = Math.abs(l - prevL);
        const dr = Math.abs(r - prevR);
        if ((dl > thr && Math.abs(l) > 0.2) || (dr > thr && Math.abs(r) > 0.2)) {
          clicks++;
        }
        prevL = l;
        prevR = r;
      }

      if (clicks >= 10) _glitchPoints += 2;
      else if (clicks >= 4) _glitchPoints += 1;

      const now = Date.now();
      if (now - _lastGlitchReportMs >= 1000) {
        _lastGlitchReportMs = now;
        if (_glitchPoints >= 6 && onGlitch) {
          try {
            onGlitch({ points: _glitchPoints, clicks, rms });
          } catch (_) {}
        }
        _glitchPoints = Math.max(0, _glitchPoints - 2);
      }
    } catch (_) {}
  }

  // ============ ScriptProcessor 回调 ============
  node.onaudioprocess = (event) => {
    try {
      const { inputBuffer, outputBuffer } = event;
      const input = [
        inputBuffer.getChannelData(0),
        inputBuffer.getChannelData(1)
      ];
      const output = [
        outputBuffer.getChannelData(0),
        outputBuffer.getChannelData(1)
      ];
      const frameCount = input[0].length;
      const heap = _wasm.HEAPF32;

      // 空间关闭时直通
      const spatialVal = Number(_cache.spatial);
      if (!Number.isFinite(spatialVal) || spatialVal < 0.001) {
        output[0].set(input[0]);
        output[1].set(input[1] ?? input[0]);
        _applyHardLimiter(output[0], output[1], frameCount);
        _detectGlitch(output[0], output[1], frameCount);
        return;
      }

      // 虚拟环绕模式
      if (_spatialDeconstruction && input[1]) {
        const ENGINE_IN_GAIN = 0.5;
        const inputL = input[0];
        const inputR = input[1];
        const outputL = output[0];
        const outputR = output[1];

        const focusTarget = Math.max(0, Math.min(1, Number(_cache.vocalFocus ?? 0.15)));
        if (_vocalFocusSmoothed == null) _vocalFocusSmoothed = focusTarget;
        _vocalFocusSmoothed += (focusTarget - _vocalFocusSmoothed) * 0.05;
        const focus = _vocalFocusSmoothed;

        _ensureDeconstructionScratch(frameCount);
        const midSignal = _tmpMid;
        for (let i = 0; i < frameCount; i++) {
          midSignal[i] = (inputL[i] + inputR[i]) * 0.5 * ENGINE_IN_GAIN;
        }

        _ensureSurroundBuffers(frameCount);
        const inLP = _surroundInLPtr;
        const inRP = _surroundInRPtr;
        const outLP = _surroundOutLPtr;
        const outRP = _surroundOutRPtr;

        _copyToHeapScaled(inputL, inLP, frameCount, ENGINE_IN_GAIN);
        _copyToHeapScaled(inputL, inRP, frameCount, ENGINE_IN_GAIN);
        _engineSurroundL.processStereo(inLP, inRP, outLP, outRP, frameCount);

        const resL_L = _tmpResLL;
        const resL_R = _tmpResLR;
        {
          const baseL = outLP / 4;
          const baseR = outRP / 4;
          for (let i = 0; i < frameCount; i++) {
            resL_L[i] = heap[baseL + i] || 0;
            resL_R[i] = heap[baseR + i] || 0;
          }
        }

        _copyToHeapScaled(inputR, inLP, frameCount, ENGINE_IN_GAIN);
        _copyToHeapScaled(inputR, inRP, frameCount, ENGINE_IN_GAIN);
        _engineSurroundR.processStereo(inLP, inRP, outLP, outRP, frameCount);
        const resR_L = heap.subarray(outLP / 4, outLP / 4 + frameCount);
        const resR_R = heap.subarray(outRP / 4, outRP / 4 + frameCount);

        const width = 1.2;
        const airComp = 0.08;
        const finalGain = 1.25;
        const midGain = 0.10 + 0.90 * focus;
        const spatialGain = 0.90 - 0.35 * focus;

        for (let i = 0; i < frameCount; i++) {
          // 左虚拟音箱 (-55°) 的处理
          const spatialL = resL_L[i];
          // 右虚拟音箱 (+55°) 的处理
          const spatialR = resR_R[i];
          
          const sideSignal = (inputL[i] - inputR[i]) * 0.3 * width * ENGINE_IN_GAIN;
          const brightnessL = inputL[i] * airComp * ENGINE_IN_GAIN;
          const brightnessR = inputR[i] * airComp * ENGINE_IN_GAIN;

          const outL = (spatialL * spatialGain) + (midSignal[i] * midGain) + sideSignal + brightnessL;
          const outR = (spatialR * spatialGain) + (midSignal[i] * midGain) - sideSignal + brightnessR;

          outputL[i] = outL * finalGain;
          outputR[i] = outR * finalGain;
        }

        // 注：resR_L 是右虚拟音箱的左通道输出，在当前的立体声合成中
        // 隐含地被用在了空间分离的模式中（虽然显式变量未直接引用）
        // 保留以供未来的立体声增强算法使用
        void resR_L;
        _applyHardLimiter(outputL, outputR, frameCount);
        _detectGlitch(outputL, outputR, frameCount);
        return;
      }

      // 常规点声源模式
      if (!_staticBuffers || _lastFrameCount !== frameCount) {
        _initStaticBuffers(frameCount);
      }
      const sb = _staticBuffers;

      _copyToHeapScaled(input[0], sb.inL, frameCount, 1.0);
      const inRSource = input[1] ?? input[0];
      _copyToHeapScaled(inRSource, sb.inR, frameCount, 1.0);

      _fidelity.process(sb.inL, frameCount);
      _fidelity.process(sb.inR, frameCount);
      _engine.processStereo(sb.inL, sb.inR, sb.outL, sb.outR, frameCount);

      output[0].set(heap.subarray(sb.outL / 4, sb.outL / 4 + frameCount));
      output[1].set(heap.subarray(sb.outR / 4, sb.outR / 4 + frameCount));
      _applyHardLimiter(output[0], output[1], frameCount);
      _detectGlitch(output[0], output[1], frameCount);
    } catch (err) {
      console.error('[TUAC] ScriptProcessor error:', err);
    }
  };

  // ============ 导出 API ============
  return {
    node,
    setParams(params = {}) {
      if (params.warmth !== undefined || params.thickness !== undefined) {
        _cache.warmth = params.warmth ?? _cache.warmth;
        _cache.thickness = params.thickness ?? _cache.thickness;
        _syncAllEngines(e => e.setWarmthParams(_cache.warmth, _cache.thickness));
      }
      if (params.azimuth !== undefined) {
        _cache.azimuth = params.azimuth;
        if (_engine) _engine.setAzimuth(params.azimuth);
      }
      if (params.elevation !== undefined) {
        _cache.elevation = params.elevation;
        _syncAllEngines(e => e.setElevation(params.elevation));
      }
      if (params.distance !== undefined) {
        _cache.distance = params.distance;
        _syncAllEngines(e => e.setDistance(params.distance));
        if (params.airHighPass === undefined) {
          const bypassAirAbsorption = params.distance < 3.0;
          const airHighPass = bypassAirAbsorption ? 1.0 : 0.85;
          _cache.airHighPass = airHighPass;
          _syncAllEngines(e => {
            if (typeof e.setAirHighPass === 'function') e.setAirHighPass(airHighPass);
          });
        }
      }
      if (params.airHighPass !== undefined) {
        _cache.airHighPass = params.airHighPass;
        _syncAllEngines(e => {
          if (typeof e.setAirHighPass === 'function') e.setAirHighPass(params.airHighPass);
        });
      }
      if (params.smoothing !== undefined) {
        _cache.smoothing = params.smoothing;
        _syncAllEngines(e => {
          if (typeof e.setSmoothing === 'function') e.setSmoothing(params.smoothing);
        });
      }
      if (params.roomDepth !== undefined) {
        _cache.roomDepth = params.roomDepth;
        _syncAllEngines(e => {
          if (typeof e.setRoomDepth === 'function') e.setRoomDepth(params.roomDepth);
        });
      }
      if (params.humidity !== undefined) {
        _cache.humidity = params.humidity;
        _syncAllEngines(e => e.setHumidity(params.humidity));
      }
      if (params.spatial !== undefined) {
        _cache.spatial = params.spatial;
        _syncAllEngines(e => e.setWetLevel(params.spatial));
      }
      if (params.bassIntensity !== undefined) {
        _cache.bass = params.bassIntensity;
        _syncAllEngines(e => e.setBassIntensity(params.bassIntensity));
      }
      if (params.inputGain !== undefined) {
        _cache.inputGain = params.inputGain;
        _syncAllEngines(e => e.setInputGain(params.inputGain));
      }
      if (params.vocalFocus !== undefined) {
        _cache.vocalFocus = Math.max(0, Math.min(1, params.vocalFocus));
      }
      if (params.eqEnabled !== undefined) {
        _cache.eqEnabled = Boolean(params.eqEnabled);
        _syncAllEngines(e => {
          if (typeof e.setEQEnabled === 'function') e.setEQEnabled(_cache.eqEnabled);
        });
      }
      if (params.fidelity !== undefined && _fidelity) {
        _fidelity.setIntensity(params.fidelity);
      }
      if (params.spatialDeconstruction !== undefined) {
        _spatialDeconstruction = !!params.spatialDeconstruction;
      }
    },
    loadIRChannel(index, pcmData) {
      if (!_wasm || !pcmData || !pcmData.length) return;
      const len = pcmData.length;
      const malloc = _wasm._custom_malloc;
      const free = _wasm._custom_free;
      const ptr = malloc(len * 4);
      if (!ptr) return;
      try {
        _wasm.HEAPF32.set(pcmData, ptr / 4);
        if (_engine) _engine.setIRChannel(index, ptr, len);
        if (_engineSurroundL) _engineSurroundL.setIRChannel(index, ptr, len);
        if (_engineSurroundR) _engineSurroundR.setIRChannel(index, ptr, len);
      } finally {
        free(ptr);
      }
    },
    destroy() {
      _releaseStaticBuffers();
      if (_wasm && _wasm._custom_free) {
        const f = _wasm._custom_free;
        if (_surroundInLPtr) f(_surroundInLPtr);
        if (_surroundInRPtr) f(_surroundInRPtr);
        if (_surroundOutLPtr) f(_surroundOutLPtr);
        if (_surroundOutRPtr) f(_surroundOutRPtr);
      }
      try {
        node.disconnect();
      } catch (_) {}
    }
  };
}
